import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
    Box,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Typography,
    Radio,
    Stack,
    IconButton
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useTranslation } from 'react-i18next';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import dayjs from 'dayjs';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import {
    esFechaSeguimientoPosteriorARegularizaciones,
    getFechaMinimaSeguimiento,
    getFechaRegularizacionMasTardia
} from '../utils/fechaSeguimientoVerosimilitud';

interface VerosimilitudProps {
    mode: 'create' | 'edit';
    secciones: VerosimilitudSection[];
    respuestas: any[];
    setRespuestas: React.Dispatch<React.SetStateAction<any[]>>;
    fechaFinContrato?: string | null;
    tipoId?: number;
}

type VerosimilitudQuestion = {
    id: number;
    orden: number;
    texto: string;
    tiposRespuesta: {
        id: number;
        descripcion: string;
        requiereFecha: boolean;
        tipoComponente: string;
        valorDefecto: boolean;
        orden: number;
        selected?: boolean;
        fecha?: string | null;
    }[];
    norma: string;
    recomendaciones?: { id: number; idTipoRespuesta: number; texto: string; orden?: number }[];
};

type VerosimilitudSection = {
    id: number;
    titulo: string;
    preguntas: VerosimilitudQuestion[];
};

interface VerosimilitudSeleccion {
    idPregunta: number;
    idSeccion?: number;
    opciones: {
        id: number;
        selected: boolean;
    }[];
    fechaRegularizacion?: string | null;
    fechaSeguimiento?: string | null;
    observacion?: string;
}

type AnswerLabels = {
    cumple: string;
    incumple: string;
    noAplica: string;
};

const getSelectedRgrlAnswer = (pregunta: VerosimilitudQuestion) =>
    pregunta.tiposRespuesta.find((respuesta) => respuesta.selected);

const formatRgrlAnswer = (descripcion?: string) => {
    if (!descripcion) return '-';

    const normalized = descripcion.trim().toUpperCase();

    if (normalized === 'N/A' || normalized === 'NO APLICA') {
        return 'NA';
    }

    return normalized;
};

// El back envía `fecha` como datetime ISO en UTC (ej: "2026-10-06T00:00:00.000Z").
// Parsear ese string completo con dayjs lo convierte a hora local y puede retroceder
// un día (ej: UTC-3 vuelve 2026-10-06T00:00Z en 2026-10-05 21:00 local). Por eso
// siempre se trunca a los primeros 10 caracteres (YYYY-MM-DD) antes de parsear.
const normalizeRgrlDate = (fecha?: string | null) => {
    if (!fecha) return null;

    const parsedDate = dayjs(fecha.substring(0, 10));

    return parsedDate.isValid() ? parsedDate : null;
};

const formatRgrlDate = (fecha?: string | null) => {
    const parsedDate = normalizeRgrlDate(fecha);

    return parsedDate ? parsedDate.format('DD/MM/YYYY') : '-';
};

const getIncumpleQuestionId = (selection: VerosimilitudSeleccion[]) =>
    selection.find((item) => item.opciones.some((opcion) => opcion.id === 2 && opcion.selected))
        ?.idPregunta;

type VerosimilitudQuestionAccordionProps = {
    pregunta: VerosimilitudQuestion;
    selection?: VerosimilitudSeleccion;
    expanded: boolean;
    labels: AnswerLabels;
    regulationDateLabel: string;
    recommendationsLabel: string;
    noRecommendationsAvailableLabel: string;
    showRecomendaciones: boolean;
    onAccordionChange: (id: number) => (_: any, isExpanded: boolean) => void;
    onAnswerChange: (idPregunta: number, opcionId: number) => void;
    onDateChange: (
        idPregunta: number,
        field: 'fechaRegularizacion',
        fecha: string
    ) => void;
};

const VerosimilitudQuestionAccordion = React.memo(
    ({
        pregunta,
        selection,
        expanded,
        labels,
        regulationDateLabel,
        recommendationsLabel,
        noRecommendationsAvailableLabel,
        showRecomendaciones,
        onAccordionChange,
        onAnswerChange,
        onDateChange
    }: VerosimilitudQuestionAccordionProps) => {
        const selectedValue =
            selection?.opciones.find((op) => op.selected)?.id.toString() || '3';
        const isIncumple = selectedValue === '2';
        const selectedOptionId = selection?.opciones.find((op) => op.selected)?.id;
        const recomendacionAplicable = pregunta.recomendaciones?.find(
            // idTipoRespuesta ?? 2: el back aún no siempre lo envía; todas las
            // recomendaciones actuales son para "No cumple" (id 2).
            (r) => (r.idTipoRespuesta ?? 2) === selectedOptionId
        );
        const isDateError = (field: 'fechaRegularizacion') => {
            if (!isIncumple) return false;
            const value = selection?.[field];
            if (!value) return true;
            return Boolean(normalizeRgrlDate(value)?.isBefore(dayjs(), 'day'));
        };
        const selectedRgrlAnswer = getSelectedRgrlAnswer(pregunta);
        const rgrlRegularizationDate =
            selectedRgrlAnswer?.fecha || pregunta.tiposRespuesta.find((respuesta) => respuesta.fecha)?.fecha;
        const handleAccordionToggle = (_: any, isExpanded: boolean) => {
            if (!isExpanded && isIncumple) {
                return;
            }

            onAccordionChange(pregunta.id)(_, isExpanded);
        };
        const getLabel = (opId: number) => {
            if (opId === 1) return labels.cumple;
            if (opId === 2) return labels.incumple;
            return labels.noAplica;
        };

        return (
            <Accordion
                expanded={expanded}
                onChange={handleAccordionToggle}
                disableGutters
                sx={{
                    border: '1px solid #E0E0E0',
                    boxShadow: 'none',
                    '&:before': { display: 'none' }
                }}
            >
                <AccordionSummary
                    sx={{
                        backgroundColor: 'white',
                        color: '#384A52'
                    }}
                >
                    <Box
                        sx={{
                            display: 'grid',
                            gridTemplateColumns: 'minmax(0, 7.5fr) minmax(0, 1.5fr) minmax(0, 1.5fr) minmax(0, 1.5fr)',
                            width: '100%',
                            gap: 2,
                            alignItems: 'center',
                        }}
                    >
                        <Stack
                            direction="row"
                            spacing={1.5}
                            alignItems="flex-start"
                            sx={{ pr: 2 }}
                        >
                            <Typography sx={{ fontWeight: 600 }}>{pregunta.orden}</Typography>
                            <Typography>{pregunta.texto}</Typography>
                        </Stack>

                        <Typography
                            variant="body2"
                            sx={{
                                textAlign: 'left',
                                color: 'text.secondary'
                            }}
                        >
                            {formatRgrlAnswer(selectedRgrlAnswer?.descripcion)}
                        </Typography>

                        <Typography
                            variant="body2"
                            sx={{
                                textAlign: 'left',
                                color: 'text.secondary'
                            }}
                        >
                            {formatRgrlDate(rgrlRegularizationDate)}
                        </Typography>

                        <Stack
                            direction="row"
                            sx={{
                                justifyContent: 'space-between'
                            }}
                            onClick={(e) => e.stopPropagation()}
                            onFocus={(e) => e.stopPropagation()}
                        >
                            {[1, 2, 3].map((opId) => (
                                <Radio
                                    key={opId}
                                    checked={selectedValue === String(opId)}
                                    inputProps={{ 'aria-label': getLabel(opId) }}
                                    onChange={() => onAnswerChange(pregunta.id, opId)}
                                    sx={{
                                        width: 44,
                                        p: 0.5,
                                        color: '#384A52',
                                        '&.Mui-checked': {
                                            color: '#384A52'
                                        },
                                        '& .MuiSvgIcon-root': {
                                            fontSize: 26
                                        }
                                    }}
                                />
                            ))}
                        </Stack>
                    </Box>
                </AccordionSummary>

                {isIncumple && (
                    <AccordionDetails>
                        <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="es">
                            <Box
                                sx={{
                                    display: 'grid',
                                    gridTemplateColumns: {
                                        xs: '1fr',
                                        md: 'repeat(24, minmax(0, 1fr))'
                                    },
                                    gap: 2,
                                    pt: 1,
                                    width: '100%',
                                    justifyItems: 'stretch'
                                }}
                            >
                                {showRecomendaciones && (
                                <Box sx={{ gridColumn: { xs: '1', md: '2 / span 13' } }}>
                                    <Typography sx={{ fontWeight: 600, mb: 0.5 }}>
                                        {recommendationsLabel}
                                    </Typography>
                                    {recomendacionAplicable ? (
                                        <Typography variant="body2">{recomendacionAplicable.texto}</Typography>
                                    ) : (
                                        <Typography variant="body2" color="text.secondary">
                                            {noRecommendationsAvailableLabel}
                                        </Typography>
                                    )}
                                </Box>
                                )}

                                <Box sx={{ gridColumn: { xs: '1', md: '15 / span 10' } }}>
                                    <DatePicker
                                        label={regulationDateLabel}
                                        // Se trunca a YYYY-MM-DD (el modo edición trae datetime UTC), pero
                                        // sin pasar a null una fecha a medio tipear: si el valor controlado
                                        // cambia a null, el DatePicker borra lo que el preventor está escribiendo.
                                        value={
                                            selection?.fechaRegularizacion
                                                ? dayjs(selection.fechaRegularizacion.substring(0, 10))
                                                : null
                                        }
                                        onChange={(newDate) => {
                                            const formattedDate = newDate
                                                ? dayjs(newDate).format('YYYY-MM-DD')
                                                : '';
                                            onDateChange(
                                                pregunta.id,
                                                'fechaRegularizacion',
                                                formattedDate
                                            );
                                        }}
                                        slotProps={{
                                            textField: {
                                                size: 'small',
                                                fullWidth: true,
                                                error: isDateError('fechaRegularizacion')
                                            }
                                        }}
                                        format="DD/MM/YYYY"
                                        disablePast={true}
                                    />
                                </Box>
                            </Box>
                        </LocalizationProvider>
                    </AccordionDetails>
                )}
            </Accordion>
        );
    }
);

const VerosimilitudGrid: React.FC<VerosimilitudProps> = ({ secciones, mode, fechaFinContrato, tipoId }) => {
    const { t } = useTranslation('visitas');
    const [verosimilitudSeleccion, setVerosimilitudSeleccion] = useState<VerosimilitudSeleccion[]>(
        []
    );
    const [fechaSeguimientoUnica, setFechaSeguimientoUnica] = useState<string | null>(null);
    const [expandedSection, setExpandedSection] = useState<number | false>(false);
    const [expandedQuestion, setExpandedQuestion] = useState<number | false>(false);

    const hayItemsIncumplidos = useMemo(
        () =>
            verosimilitudSeleccion.some((registro) =>
                registro.opciones.some((op) => op.id === 2 && op.selected)
            ),
        [verosimilitudSeleccion]
    );

    const topeFechaSeguimiento = useMemo(() => {
        if (!fechaFinContrato) return null;
        const tope = dayjs(fechaFinContrato).subtract(3, 'month');
        return tope.isValid() ? tope : null;
    }, [fechaFinContrato]);

    // SMP-430: la fecha de seguimiento debe ser al menos un día posterior a la
    // regularización más tardía de los ítems "incumple".
    const regularizacionMasTardia = useMemo(
        () => getFechaRegularizacionMasTardia(verosimilitudSeleccion),
        [verosimilitudSeleccion]
    );

    const fechaMinimaSeguimiento = useMemo(
        () => getFechaMinimaSeguimiento(verosimilitudSeleccion, dayjs()),
        [verosimilitudSeleccion]
    );

    const fechaSeguimientoError = useMemo(() => {
        if (!hayItemsIncumplidos) return null;
        if (!fechaSeguimientoUnica) {
            return String(t('verosimilitud.form.followUpDateRequired'));
        }
        if (normalizeRgrlDate(fechaSeguimientoUnica)?.isBefore(dayjs(), 'day')) {
            return String(t('verosimilitud.form.followUpDateInThePast'));
        }
        if (
            !esFechaSeguimientoPosteriorARegularizaciones(
                fechaSeguimientoUnica,
                verosimilitudSeleccion
            )
        ) {
            return String(
                t('verosimilitud.form.followUpDateNotAfterRegularization', {
                    fecha: formatRgrlDate(regularizacionMasTardia)
                })
            );
        }
        return null;
    }, [hayItemsIncumplidos, fechaSeguimientoUnica, verosimilitudSeleccion, regularizacionMasTardia, t]);

    // RN-09: excede el tope de fecha_fin_contrato - 3 meses. NO bloquea el guardado
    // (decision de producto, 2026-07-31) - se muestra como advertencia, no como error.
    const fechaSeguimientoWarning = useMemo(() => {
        if (!hayItemsIncumplidos || fechaSeguimientoError || !fechaSeguimientoUnica) return null;
        if (topeFechaSeguimiento && normalizeRgrlDate(fechaSeguimientoUnica)?.isAfter(topeFechaSeguimiento, 'day')) {
            return String(t('verosimilitud.form.followUpDateExceedsLimit'));
        }
        return null;
    }, [hayItemsIncumplidos, fechaSeguimientoError, fechaSeguimientoUnica, topeFechaSeguimiento, t]);

    const handleSectionAccordionChange = useCallback((id: number) => (_: any, isExpanded: boolean) => {
        setExpandedSection(isExpanded ? id : false);
    }, []);

    const handleQuestionAccordionChange = useCallback((id: number) => (_: any, isExpanded: boolean) => {
        setExpandedQuestion(isExpanded ? id : false);
    }, []);

    const getDefaultOptionId = (pregunta: VerosimilitudProps['secciones'][number]['preguntas'][number]) => {
        const selectedResponse = pregunta.tiposRespuesta.find((r) => r.selected);
        const defaultResponse = pregunta.tiposRespuesta.find((r) => r.valorDefecto);
        return selectedResponse?.id ?? defaultResponse?.id ?? 3;
    };

    useEffect(() => {
        if (secciones.length === 0) return;

        const stored = localStorage.getItem('verosimilitud');
        const storedFechaSeguimientoUnica = localStorage.getItem('verosimilitudFechaSeguimiento');
        if (storedFechaSeguimientoUnica) {
            setFechaSeguimientoUnica(storedFechaSeguimientoUnica);
        }

        if (mode === 'edit') {
            if (stored) {
                const parsedSelection = JSON.parse(stored);
                setVerosimilitudSeleccion(parsedSelection);
                setExpandedQuestion(getIncumpleQuestionId(parsedSelection) ?? false);
            }
            return;
        }

        if (stored) {
            const parsedSelection = JSON.parse(stored);
            setVerosimilitudSeleccion(parsedSelection);
            setExpandedQuestion(getIncumpleQuestionId(parsedSelection) ?? false);
            return;
        }

        const initial: VerosimilitudSeleccion[] = secciones.flatMap((seccion) =>
            seccion.preguntas.map((pregunta) => {
                const selectedOptionId = getDefaultOptionId(pregunta);
                const selectedRgrlAnswer = getSelectedRgrlAnswer(pregunta);
                const rgrlRegularizationDate =
                    selectedRgrlAnswer?.fecha ??
                    pregunta.tiposRespuesta.find((respuesta) => respuesta.fecha)?.fecha ??
                    null;

                return {
                    idPregunta: pregunta.id,
                    idSeccion: seccion.id,
                    opciones: [1, 2, 3].map((id) => ({
                        id,
                        selected: id === selectedOptionId
                    })),
                    // RN-02: se precarga la fecha declarada en el RGRL. RN-03: queda editable (DatePicker en AccordionDetails).
                    // Se normaliza a YYYY-MM-DD (ver normalizeRgrlDate) para que el DatePicker
                    // no reinterprete el datetime UTC del back y corra la fecha un día.
                    fechaRegularizacion: normalizeRgrlDate(rgrlRegularizationDate)?.format('YYYY-MM-DD') ?? null,
                    fechaSeguimiento: null
                };
            })
        );

        setVerosimilitudSeleccion(initial);
        setExpandedQuestion(getIncumpleQuestionId(initial) ?? false);
    // secciones omitido intencionalmente: el padre garantiza secciones.length > 0
    // antes de montar el Grid; incluirlo re-ejecutaría el efecto cada vez que
    // useRGRLVerosimilitud recomputa la referencia al agregar recomendaciones.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [mode]);

    const selectionByQuestionId = useMemo(() => {
        return verosimilitudSeleccion.reduce<Record<number, VerosimilitudSeleccion>>(
            (acc, selection) => {
                acc[selection.idPregunta] = selection;
                return acc;
            },
            {}
        );
    }, [verosimilitudSeleccion]);

    const handleAnswerChange = useCallback((idPregunta: number, opcionId: number) => {
        setVerosimilitudSeleccion((current) => {
            const hasSelection = current.some((registro) => registro.idPregunta === idPregunta);
            const nextSelection = current.map((registro) => {
                if (registro.idPregunta === idPregunta) {
                    return {
                        ...registro,
                        opciones: registro.opciones.map((op) => ({
                            ...op,
                            selected: op.id === opcionId
                        })),
                        fechaRegularizacion:
                            opcionId === 2 ? registro.fechaRegularizacion ?? null : null,
                        fechaSeguimiento: opcionId === 2 ? registro.fechaSeguimiento ?? null : null
                    };
                }
                return registro;
            });

            if (hasSelection) {
                return nextSelection;
            }

            return [
                ...nextSelection,
                {
                    idPregunta,
                    opciones: [1, 2, 3].map((id) => ({
                        id,
                        selected: id === opcionId
                    })),
                    fechaRegularizacion: null,
                    fechaSeguimiento: null
                }
            ];
        });

        if (opcionId === 2) {
            setExpandedQuestion(idPregunta);
        }
    }, []);

    useEffect(() => {
        if (verosimilitudSeleccion.length > 0) {
            localStorage.setItem('verosimilitud', JSON.stringify(verosimilitudSeleccion));
        }
    }, [verosimilitudSeleccion]);

    useEffect(() => {
        if (fechaSeguimientoUnica) {
            localStorage.setItem('verosimilitudFechaSeguimiento', fechaSeguimientoUnica);
        } else {
            localStorage.removeItem('verosimilitudFechaSeguimiento');
        }
    }, [fechaSeguimientoUnica]);

    const handleFechaSeguimientoUnicaChange = useCallback((newDate: dayjs.Dayjs | null) => {
        setFechaSeguimientoUnica(newDate && newDate.isValid() ? newDate.format('YYYY-MM-DD') : null);
    }, []);

    const handleDateChange = useCallback((
        idPregunta: number,
        field: 'fechaRegularizacion',
        fecha: string
    ) => {
        setVerosimilitudSeleccion((current) =>
            current.map((registro) => {
                if (registro.idPregunta === idPregunta) {
                    return {
                        ...registro,
                        [field]: fecha
                    };
                }
                return registro;
            })
        );
    }, []);

    const answerLabels = useMemo(
        () => ({
            cumple: String(t('verosimilitud.radioLabels.cumple')),
            incumple: String(t('verosimilitud.radioLabels.incumple')),
            noAplica: String(t('verosimilitud.radioLabels.noAplica'))
        }),
        [t]
    );

    const regulationDateLabel = String(t('verosimilitud.columns.regulationDate'));
    const followUpDateLabel = String(t('verosimilitud.columns.followUpDate'));
    const recommendationsLabel = String(t('verosimilitud.form.recommendations'));
    const noRecommendationsAvailableLabel = String(
        t('verosimilitud.form.noRecommendationsAvailable')
    );

    return (
        <Box>
            {hayItemsIncumplidos && (
                <Box px={2} py={2} mb={1} sx={{ border: '1px solid #E0E0E0' }}>
                    <Typography sx={{ fontWeight: 600, mb: 1 }}>
                        {followUpDateLabel}
                    </Typography>
                    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="es">
                        <Box>
                            <DatePicker
                                label={followUpDateLabel}
                                value={normalizeRgrlDate(fechaSeguimientoUnica)}
                                onChange={handleFechaSeguimientoUnicaChange}
                                minDate={fechaMinimaSeguimiento}
                                slotProps={{
                                    textField: {
                                        size: 'small',
                                        fullWidth: true,
                                        required: true,
                                        error: Boolean(fechaSeguimientoError),
                                        helperText: fechaSeguimientoError ?? fechaSeguimientoWarning ?? undefined,
                                        FormHelperTextProps: fechaSeguimientoWarning
                                            ? { sx: { color: 'error.main' } }
                                            : undefined
                                    }
                                }}
                                format="DD/MM/YYYY"
                                disablePast={true}
                            />
                            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                                {t('verosimilitud.form.followUpDateHelper', {
                                    tope: topeFechaSeguimiento
                                        ? topeFechaSeguimiento.format('DD/MM/YYYY')
                                        : '-'
                                })}
                            </Typography>
                        </Box>
                    </LocalizationProvider>
                </Box>
            )}

            <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                px={2}
                py={4}
                mb={1}
                sx={{
                    backgroundColor: '#384A52',
                    color: 'white'
                }}
            >
                <Typography color={'white'} variant="body2" fontWeight="bold">
                    {t('verosimilitud.sections')}
                </Typography>
            </Box>

            {secciones.map((seccion) => (
                <Accordion
                    key={seccion.id}
                    expanded={expandedSection === seccion.id}
                    onChange={handleSectionAccordionChange(seccion.id)}
                >
                    <AccordionSummary
                        expandIcon={
                            <IconButton>
                                <ExpandMoreIcon sx={{ color: '#384A52' }} />
                            </IconButton>
                        }
                        sx={{
                            backgroundColor: expandedSection === seccion.id ? '#E0EBED' : 'white',
                            color: '#384A52'
                        }}
                    >
                        <Box
                            sx={{
                                width: '100%',
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center'
                            }}
                        >
                            <Typography sx={{ fontWeight: '600' }}>{seccion.titulo}</Typography>
                        </Box>
                    </AccordionSummary>

                    <AccordionDetails>
                        <Stack
                            spacing={0}
                            sx={{
                                '& > :not(style) ~ :not(style)': {
                                    marginTop: 0
                                }
                            }}
                        >
                            <Box
                                sx={{
                                    display: 'grid',
                                    gridTemplateColumns: 'minmax(0, 7.5fr) minmax(0, 1.5fr) minmax(0, 1.5fr) minmax(0, 1.5fr)',
                                    width: '100%',
                                    gap: 2,
                                    alignItems: 'center',
                                    px: 2,
                                    py: 1,
                                    backgroundColor: '#F6F6F6',
                                    border: '1px solid #E0E0E0',
                                    color: '#384A52'
                                }}
                            >
                                <Typography
                                    variant="body2"
                                    fontWeight={600}
                                >
                                    {t('verosimilitud.columns.condition')}
                                </Typography>

                                <Typography
                                    variant="body2"
                                    fontWeight={600}
                                    align="left"
                                >
                                    {t('verosimilitud.columns.response')}
                                </Typography>

                                <Typography
                                    variant="body2"
                                    fontWeight={600}
                                    align="left"
                                >
                                    {t('verosimilitud.columns.regulationDate')}
                                </Typography>

                                <Stack
                                    direction="row"
                                    sx={{
                                        justifyContent: 'space-between'
                                    }}
                                >
                                    {[1, 2, 3].map((opId) => (
                                        <Typography
                                            key={opId}
                                            variant="body2"
                                            fontWeight={600}
                                            textAlign="center"
                                            sx={{ width: 40 }}
                                        >
                                            {opId === 1
                                                ? t('verosimilitud.radioLabels.cumple')
                                                : opId === 2
                                                ? t('verosimilitud.radioLabels.incumple')
                                                : t('verosimilitud.radioLabels.noAplica')}
                                        </Typography>
                                    ))}
                                </Stack>
                            </Box>

                            {seccion.preguntas.map((pregunta) => (
                                <VerosimilitudQuestionAccordion
                                    key={pregunta.id}
                                    pregunta={pregunta}
                                    selection={selectionByQuestionId[pregunta.id]}
                                    expanded={expandedQuestion === pregunta.id}
                                    labels={answerLabels}
                                    regulationDateLabel={regulationDateLabel}
                                    recommendationsLabel={recommendationsLabel}
                                    noRecommendationsAvailableLabel={noRecommendationsAvailableLabel}
                                    showRecomendaciones={tipoId === 1}
                                    onAccordionChange={handleQuestionAccordionChange}
                                    onAnswerChange={handleAnswerChange}
                                    onDateChange={handleDateChange}
                                />
                            ))}
                        </Stack>
                    </AccordionDetails>
                </Accordion>
            ))}
        </Box>
    );
};

export default VerosimilitudGrid;
